// src/context/UserProvider.js
import React, { useState, useEffect, useContext, useRef } from "react";
import api, { apiEvents } from "../../api/axios";
import Swal from "sweetalert2";
import { UserContext } from "./Context";
import { Outlet } from "react-router-dom";

export const UserProvider = ({ children }) => {
  const [userData, setUserData] = useState({ user: null, userCompany: null });
  const [companyImage, setCompanyImage] = useState(null);
  const [companyImageRealTime, setCompanyImageRealTime] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // REF para tener siempre el valor actual dentro del handler del event emitter
  const isLoggedInRef = useRef(isLoggedIn);
  useEffect(() => {
    isLoggedInRef.current = isLoggedIn;
  }, [isLoggedIn]);

  const fetchUserData = async () => {
    console.log("[UserProvider] fetchUserData -> start");
    try {
      const url = `${process.env.REACT_APP_API_URL}`;

      const response = await api.post(url + "/getuser");
      console.log("[UserProvider] /getuser OK:", response);

      const { user, userCompany, token, isSuperAdmin } = response.data || {};

      console.log(response.data);
      if (user) {
        setUserData({ user, userCompany, token, isSuperAdmin });
        setIsLoggedIn(true);
      } else {
        setUserData({ user: null, userCompany: null });
        setIsLoggedIn(false);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        console.warn("[UserProvider] /getuser 401 - no autenticado");
      } else {
        console.error("[UserProvider] Error fetching user data:", error);
      }
      setUserData({ user: null, userCompany: null });
      setIsLoggedIn(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Logout unificado (manual o automático). Flag 'auto' opcional.
  const logout = async (showConfirm = false, auto = false) => {
    const doLogout = async () => {
      // 🔄 Swal de carga
      await Swal.fire({
        title: "Cerrando sesión...",
        html: "Por favor espera",
        timer: 1000,
        timerProgressBar: true,
        allowOutsideClick: false,
        showCancelButton: false,
        showConfirmButton: false,
        // didOpen: () => {
        //   Swal.showLoading();
        // },
      });

      try {
        // Solo llamar al backend si estamos realmente logueados
        if (isLoggedInRef.current) {
          await api.post("/logout");
        }
      } catch (err) {
        console.warn("[UserProvider] Logout backend falló", err);
      }

      if (auto) {
        await Swal.fire({
          title: "Sesión expirada",
          text: "Tu sesión ha caducado, por favor vuelve a iniciar sesión.",
          icon: "warning",
          timer: 2000,
          showConfirmButton: false,
          allowOutsideClick: false,
        });
      }

      // 🧹 Limpieza total
      sessionStorage.clear();
      localStorage.clear();

      // 🧠 Limpiar estado React SIEMPRE
      setUserData({ user: null, userCompany: null });
      setIsLoggedIn(false);

      // window.location.replace("/");
    };

    if (showConfirm) {
      const result = await Swal.fire({
        title: "Cerrar sesión",
        icon: "warning",
        html: `<p>¡Estás a punto de salir!</p><b>¿Deseas continuar?</b>`,
        showCancelButton: true,
        confirmButtonText: "Sí",
        confirmButtonColor: "#1ab394",
        cancelButtonText: "No, volver",
        cancelButtonColor: "#d33",
        reverseButtons: true,
      });

      if (result.isConfirmed) {
        doLogout();
      }
    } else {
      doLogout();
    }
  };

  const updateCompanyImage = (image) => setCompanyImage(image);
  const updateCompanyImageRealTime = (image) => setCompanyImageRealTime(image);

  useEffect(() => {
    fetchUserData();
  }, []);

  useEffect(() => {
    const handleUnauthorized = () => {
      console.log(
        "[UserProvider] apiEvents unauthorized received — isLoggedInRef:",
        isLoggedInRef.current,
      );
      // Solo hacer logout automático si el usuario estaba realmente logueado
      if (isLoggedInRef.current) {
        // pasar `auto = true` si quieres mostrar Swal y luego redirigir
        logout(false, true);
      } else {
        console.log(
          "[UserProvider] Ignorado unauthorized porque el usuario no está logueado (inicio).",
        );
      }
    };

    console.log(apiEvents);

    apiEvents.on("unauthorized", handleUnauthorized);
    return () => {
      apiEvents.off("unauthorized", handleUnauthorized);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // depende solo del montaje

  return (
    <UserContext.Provider
      value={{
        userData,
        fetchUserData,
        companyImage,
        updateCompanyImage,
        companyImageRealTime,
        updateCompanyImageRealTime,
        isLoggedIn,
        logout,
        isLoading,
      }}>
      {children ? children : <Outlet />}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
